import type { ErrorObject, ValidationRule } from '@vuelidate/core'
import { email, helpers, required } from '@vuelidate/validators'
import { z } from 'zod'
import type { IntakeDocument, IntakeDocumentSlot } from '@/api/schemas'
import {
  addressEntered,
  addressWarning,
  poBoxRegex,
  sameAddress,
  type AddressParts,
} from '@/lib/address'

// Per-step wire schemas mirroring apps/api/intake/types.go. Every field there
// is optional (json "omitempty") so a client can safely save an incomplete
// step, and the Go decoder rejects unknown fields — z.strictObject matches
// both. These schemas validate `responses[].data` when resuming a saved
// intake, and `definition.defaults`.

const BusinessHoursSchema = z.strictObject({
  day: z.string(),
  opens: z.string().optional(),
  closes: z.string().optional(),
  closed: z.boolean().optional(),
})
export type BusinessHours = z.infer<typeof BusinessHoursSchema>

// A saved address (Address in types.go), or one free-text line saved before
// addresses were split into parts; restoreAddress() keeps that line whole.
const SavedAddressSchema = z.union([
  z.strictObject({
    street: z.string().optional(),
    unit: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: z.string().optional(),
  }),
  z.string(),
])

const PrerequisitesSchema = z.strictObject({
  gbp_exists: z.boolean().optional(),
  gbp_verified: z.boolean().optional(),
  gbp_public: z.boolean().optional(),
  gbp_address: SavedAddressSchema.optional(),
  gbp_po_box: z.boolean().optional(),
})

// One owner row on the Burns Legal entity step (BusinessOwner in types.go).
const BusinessOwnerSchema = z.strictObject({
  full_name: z.string().optional(),
  title: z.string().optional(),
  email: z.string().optional(),
})
type BusinessOwner = z.infer<typeof BusinessOwnerSchema>

const BusinessIdentitySchema = z.strictObject({
  legal_name: z.string().optional(),
  dba: z.string().optional(),
  entity_type: z.string().optional(),
  formation_state: z.string().optional(),
  formation_date: z.string().optional(),
  ein: z.string().optional(),
  physical_address: SavedAddressSchema.optional(),
  mailing_same_as_physical: z.boolean().optional(),
  mailing_address: SavedAddressSchema.optional(),
  main_phone: z.string().optional(),
  business_email: z.string().optional(),
  website: z.string().optional(),
  years_in_business: z.number().optional(),
  duns: z.string().optional(),
  owners: z.array(BusinessOwnerSchema).optional(),
  owners_background_check_acknowledged: z.boolean().optional(),
})

const OwnerContactSchema = z.strictObject({
  owner_legal_name: z.string().optional(),
  title: z.string().optional(),
  direct_phone: z.string().optional(),
  direct_email: z.string().optional(),
  background_check_contact: z.string().optional(),
  background_check_acknowledged: z.boolean().optional(),
})

const ServicesSchema = z.strictObject({
  primary_lsa_category: z.string().optional(),
  broad_category_leads: z.boolean().optional(),
  wanted_job_types: z.array(z.string()).optional(),
  other_wanted_work: z.string().optional(),
  wanted_work_capability_acknowledged: z.boolean().optional(),
  unwanted_job_types: z.array(z.string()).optional(),
  other_unwanted_work: z.string().optional(),
  minimum_job_size: z.string().optional(),
  work_refused: z.string().optional(),
})

const ServiceAreaSchema = z.strictObject({
  primary_city: z.string().optional(),
  zip_codes: z.array(z.string()).optional(),
  counties: z.array(z.string()).optional(),
  max_drive_distance: z.number().optional(),
  exclusions: z.string().optional(),
})

const HoursResponsivenessSchema = z.strictObject({
  hours: z.array(BusinessHoursSchema).optional(),
  answered_by: z.string().optional(),
  after_hours_handling: z.string().optional(),
  calls_recorded_acknowledged: z.boolean().optional(),
})

const BudgetSchema = z.strictObject({
  weekly_budget: z.number().optional(),
  target_leads_per_week: z.number().optional(),
  average_job_value: z.number().optional(),
})

const LicenseSchema = z.strictObject({
  jurisdiction: z.string().optional(),
  type: z.string().optional(),
  number: z.string().optional(),
  expiry: z.string().optional(),
})
type License = z.infer<typeof LicenseSchema>

const LocalRequirementSchema = z.strictObject({
  jurisdiction: z.string().optional(),
  work_accepted: z.string().optional(),
  registration_required: z.boolean().optional(),
  registration_held: z.boolean().optional(),
  authority: z.string().optional(),
  number: z.string().optional(),
  expiry: z.string().optional(),
})
type LocalRequirement = z.infer<typeof LocalRequirementSchema>

const LicensesSchema = z.strictObject({
  licenses: z.array(LicenseSchema).optional(),
  local_requirements: z.array(LocalRequirementSchema).optional(),
})

const InsuranceSchema = z.strictObject({
  general_liability_carrier: z.string().optional(),
  policy_number: z.string().optional(),
  coverage_limits: z.string().optional(),
  policy_expiry: z.string().optional(),
  has_employees: z.boolean().optional(),
  workers_comp_carrier: z.string().optional(),
  workers_comp_policy_number: z.string().optional(),
  workers_comp_expiry: z.string().optional(),
})

const DocumentsStepSchema = z.strictObject({
  available_documents: z.array(z.string()).optional(),
  name_address_consistency_acknowledged: z.boolean().optional(),
  local_licenses_confirmed: z.boolean().optional(),
  notes: z.string().optional(),
})

const GoogleAccessSchema = z.strictObject({
  owner_google_account: z.string().optional(),
  manager_access_acknowledged: z.boolean().optional(),
  gbp_access_available: z.boolean().optional(),
  existing_google_ads_account: z.boolean().optional(),
  existing_google_ads_account_details: z.string().optional(),
})

const ReviewSchema = z.strictObject({
  review_acknowledged: z.boolean().optional(),
  notes: z.string().optional(),
})

const LSAContactSchema = z.strictObject({
  lsa_call_phone: z.string().optional(),
})

const BurnsServicesSchema = z.strictObject({
  wanted_services: z.array(z.string()).optional(),
  other_wanted_work: z.string().optional(),
  minimum_job_size: z.string().optional(),
  wanted_work_capability_acknowledged: z.boolean().optional(),
})

const BurnsHoursSchema = z.strictObject({
  business_hours: z.array(BusinessHoursSchema).optional(),
  ad_hours: z.array(BusinessHoursSchema).optional(),
})

// Defaults for the Burns Construction definition (apps/api/intake/definitions.go).
// Loose z.object: unknown keys are stripped rather than rejected, since this
// is server-provided data, not a client payload.
export const BurnsDefaultsSchema = z.object({
  brand_name: z.string().optional(),
  service_area: z.string().optional(),
  monthly_starting_budget: z.number().optional(),
  gbp_arrangement: z.string().optional(),
  notification_email: z.string().optional(),
  review_url: z.string().optional(),
})
export type BurnsDefaults = z.infer<typeof BurnsDefaultsSchema>

// --- form data ---
//
// This is the combined, flat working model the renderer mutates across every
// step of either definition (Burns already carried every generic field at
// runtime before this change, so unifying the two costs nothing real). It
// reuses the wire types above for nested arrays, but differs from them in a
// few deliberate ways the wire schemas can't express:
//   - a handful of array/string fields the renderer actively mutates
//     (`.push`, `.includes`) are required here, though optional on the wire;
//   - a few fields are UI-only conveniences with no Go equivalent
//     (`zip_codes_text`, `counties_text`, `review_notes` — which avoids
//     colliding with the Documents step's own `notes` field in this flat
//     object — and `entity_type_choice`/`entity_type_other`, which
//     `serializeIdentity()` joins back into the wire's `entity_type`);
//   - numeric fields bound to plain `<input>`s (no `.number` modifier) hold
//     the raw string the browser produces until `serialize()` coerces them;
//   - each address is flat parts (`physical_address_street`, …) so every part
//     validates, shows its error, and takes focus on its own; serialize()
//     nests them back into the wire's `{ street, unit, city, state, zip }`.
export const addressParts = ['street', 'unit', 'city', 'state', 'zip'] as const
export type AddressPrefix = 'gbp_address' | 'physical_address' | 'mailing_address'
export type AddressFieldKey = `${AddressPrefix}_${(typeof addressParts)[number]}`
type AddressFormFields = Record<AddressFieldKey, string>

export interface IntakeFormData extends AddressFormFields {
  gbp_exists?: boolean | undefined
  gbp_verified?: boolean | undefined
  gbp_public?: boolean | undefined
  gbp_po_box?: boolean | undefined
  legal_name?: string | undefined
  dba?: string | undefined
  entity_type_choice: string
  entity_type_other: string
  formation_state?: string | undefined
  formation_date?: string | undefined
  ein?: string | undefined
  duns?: string | undefined
  owners: BusinessOwner[]
  owners_background_check_acknowledged?: boolean | undefined
  mailing_same_as_physical: boolean
  main_phone?: string | undefined
  business_email?: string | undefined
  website?: string | undefined
  years_in_business?: string | number | undefined
  owner_legal_name?: string | undefined
  title?: string | undefined
  direct_phone?: string | undefined
  direct_email?: string | undefined
  background_check_contact?: string | undefined
  background_check_acknowledged?: boolean | undefined
  primary_lsa_category?: string | undefined
  wanted_job_types: string[]
  unwanted_job_types: string[]
  wanted_services: string[]
  minimum_job_size?: string | undefined
  work_refused?: string | undefined
  broad_category_leads?: boolean | undefined
  other_wanted_work?: string | undefined
  other_unwanted_work?: string | undefined
  wanted_work_capability_acknowledged?: boolean | undefined
  primary_city?: string | undefined
  zip_codes_text: string
  counties_text: string
  max_drive_distance?: string | number | undefined
  exclusions?: string | undefined
  hours: BusinessHours[]
  answered_by?: string | undefined
  after_hours_handling?: string | undefined
  calls_recorded_acknowledged?: boolean | undefined
  weekly_budget?: string | number | undefined
  target_leads_per_week?: string | number | undefined
  average_job_value?: string | number | undefined
  licenses: License[]
  local_requirements: LocalRequirement[]
  general_liability_carrier?: string | undefined
  policy_number?: string | undefined
  coverage_limits?: string | undefined
  policy_expiry?: string | undefined
  has_employees?: boolean | undefined
  workers_comp_carrier?: string | undefined
  workers_comp_policy_number?: string | undefined
  workers_comp_expiry?: string | undefined
  available_documents: string[]
  name_address_consistency_acknowledged?: boolean | undefined
  local_licenses_confirmed?: boolean | undefined
  notes?: string | undefined
  owner_google_account?: string | undefined
  manager_access_acknowledged?: boolean | undefined
  gbp_access_available?: boolean | undefined
  existing_google_ads_account?: boolean | undefined
  existing_google_ads_account_details?: string | undefined
  review_acknowledged?: boolean | undefined
  review_notes: string
  lsa_call_phone?: string | undefined
  business_hours: BusinessHours[]
  ad_hours: BusinessHours[]
}

/**
 * Vuelidate rules for one step: form field name → named rules. Keys are listed
 * in on-screen order, so the first invalid key is the first field to focus.
 * Every rule message is a plain, specific sentence. Rules only decide whether
 * a step may advance or submit; saving is never blocked by them.
 */
type StepRules = Partial<Record<keyof IntakeFormData, Record<string, ValidationRule>>>

/** The part of a Vuelidate field result the intake reads. */
export interface FieldValidation {
  readonly $dirty: boolean
  readonly $error: boolean
  readonly $invalid: boolean
  readonly $errors: ErrorObject[]
  readonly $touch: () => void
  readonly $reset: () => void
}

type StepKind =
  | 'prerequisites'
  | 'identity'
  | 'owner'
  | 'services'
  | 'service-area'
  | 'generic-hours'
  | 'budget'
  | 'licenses'
  | 'insurance'
  | 'documents'
  | 'google-access'
  | 'burns-services'
  | 'burns-contact'
  | 'burns-hours'
  | 'review'

export interface IntakeStepDefinition {
  title: string
  kind: StepKind
}

// ---- Document checklist helpers, shared by the review panel, submitted view, and UploadList ----

/** Active files that count toward a slot: an exact kind match, or one of its legacy aliases. */
export function slotFiles(slot: IntakeDocumentSlot, documents: IntakeDocument[]): IntakeDocument[] {
  return documents.filter(
    (document) => document.kind === slot.kind || slot.aliases.includes(document.kind),
  )
}

/** Required slots with no matching file yet. */
export function missingRequiredSlots(
  slots: IntakeDocumentSlot[],
  documents: IntakeDocument[],
): IntakeDocumentSlot[] {
  return slots.filter((slot) => slot.need === 'required' && slotFiles(slot, documents).length === 0)
}

/** Recommended slots with no matching file yet. */
export function missingRecommendedSlots(
  slots: IntakeDocumentSlot[],
  documents: IntakeDocument[],
): IntakeDocumentSlot[] {
  return slots.filter(
    (slot) => slot.need === 'recommended' && slotFiles(slot, documents).length === 0,
  )
}

/** Uploaded files whose kind matches no current slot or alias: retired kinds and anything unrecognized. */
export function otherFiles(
  slots: IntakeDocumentSlot[],
  documents: IntakeDocument[],
): IntakeDocument[] {
  const known = new Set(slots.flatMap((slot) => [slot.kind, ...slot.aliases]))
  return documents.filter((document) => !known.has(document.kind))
}

export interface IntakeFormDefinition<T extends IntakeFormData = IntakeFormData> {
  id: string
  steps: IntakeStepDefinition[]
  initial: (defaults?: Record<string, unknown>) => T
  serialize: (step: number, form: T) => Record<string, unknown>
  restore: (step: number, form: T, data: Record<string, unknown>) => void
  blocksFirstAdvance?: (form: T) => boolean
  /** Rules for one step. Called inside a computed, so conditional rules read `form`. */
  rules: (step: number, form: T) => StepRules
}

export const entityTypes = [
  'LLC',
  'Corporation',
  'S corporation',
  'Sole proprietorship',
  'Partnership',
  'Other',
] as const

/** Splits a saved entity_type string into the select choice and the Other text. Never drops an unknown value. */
export function splitEntityType(saved: unknown): { choice: string; other: string } {
  if (typeof saved !== 'string' || saved === '') return { choice: '', other: '' }
  const match = entityTypes.find(
    (option) => option !== 'Other' && option.toLowerCase() === saved.trim().toLowerCase(),
  )
  return match ? { choice: match, other: '' } : { choice: 'Other', other: saved }
}

/** Joins the select choice and Other text back into the saved entity_type string. */
export function joinEntityType(choice: string, other: string): string {
  if (choice === 'Other') return other.trim()
  return choice
}

function ruleValid(rule: ValidationRule, value: unknown, form: IntakeFormData): boolean {
  const validator = typeof rule === 'function' ? rule : rule.$validator
  return Boolean(validator(value, form, form))
}

// helpers.withMessage() calls a function message with the field's model and
// the rule's params, which Vuelidate's own types leave out.
type RuleMessage = (props: { $model: unknown; $params: object }) => string

function ruleMessage(rule: ValidationRule, value: unknown): string {
  if (typeof rule === 'function') return ''
  const message = rule.$message
  if (typeof message === 'function') {
    const $params = '$params' in rule ? (rule.$params as object) : {}
    return (message as RuleMessage)({ $model: value, $params })
  }
  return typeof message === 'string' ? message : ''
}

/** Evaluates one step's rules against the form: field → failing messages, only for invalid fields, in rule-key order. */
export function stepErrors(
  definition: IntakeFormDefinition,
  step: number,
  form: IntakeFormData,
): Record<string, string[]> {
  const rules = definition.rules(step, form)
  const errors: Record<string, string[]> = {}
  for (const [key, fieldRules] of Object.entries(rules)) {
    const value = form[key as keyof IntakeFormData]
    for (const rule of Object.values(fieldRules)) {
      if (!ruleValid(rule, value, form)) (errors[key] ??= []).push(ruleMessage(rule, value))
    }
  }
  return errors
}

/** Indices of every step whose rules currently fail, including the review step. */
export function incompleteSteps(definition: IntakeFormDefinition, form: IntakeFormData): number[] {
  return definition.steps
    .map((_step, index) => index)
    .filter((step) => Object.keys(stepErrors(definition, step, form)).length > 0)
}

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
export const wantedJobTypes = [
  'Site preparation / clearing',
  'Excavation and grading',
  'Trenching / utility excavation',
  'Foundation or footing excavation',
  'Drainage / stormwater earthwork',
  'Demolition or removal',
]
export const unwantedJobTypes = [
  'Plumbing',
  'Electrical',
  'HVAC / mechanical',
  'Roofing',
  'Painting',
  'Remodeling / handyman work',
  'Landscaping / lawn care',
  'Tree removal',
  'Septic installation / service',
  'Paving / concrete-only work',
  'Blasting',
  'Hazardous-material remediation',
  'Emergency response',
  'Work we subcontract or cannot legally perform',
]

function hours(): BusinessHours[] {
  return days.map((day) => ({ day, opens: '', closes: '', closed: false }))
}
function strings(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((part) => part.trim())
    .filter(Boolean)
}
function number(value: unknown): number | undefined {
  return value === '' || value === undefined || value === null ? undefined : Number(value)
}
function pick<T extends object, K extends keyof T>(form: T, ...keys: K[]): Pick<T, K> {
  const out = {} as Pick<T, K>
  for (const key of keys) out[key] = form[key]
  return out
}

// ---- Addresses --------------------------------------------------------------------------------

function blankAddress<P extends AddressPrefix>(
  prefix: P,
): Record<`${P}_${(typeof addressParts)[number]}`, string> {
  return {
    [`${prefix}_street`]: '',
    [`${prefix}_unit`]: '',
    [`${prefix}_city`]: '',
    [`${prefix}_state`]: '',
    [`${prefix}_zip`]: '',
  } as Record<`${P}_${(typeof addressParts)[number]}`, string>
}

/** The address as parts, read from its flat form keys. */
export function addressOf(form: IntakeFormData, prefix: AddressPrefix): Required<AddressParts> {
  return {
    street: form[`${prefix}_street`],
    unit: form[`${prefix}_unit`],
    city: form[`${prefix}_city`],
    state: form[`${prefix}_state`],
    zip: form[`${prefix}_zip`],
  }
}

/** Restores a saved address into its flat keys. A legacy one-line string is kept whole as the street. */
function restoreAddress(
  form: IntakeFormData,
  prefix: AddressPrefix,
  saved: z.infer<typeof SavedAddressSchema> | undefined,
) {
  if (saved === undefined) return
  const parts: AddressParts = typeof saved === 'string' ? { street: saved } : saved
  for (const part of addressParts) form[`${prefix}_${part}`] = parts[part] ?? ''
}

/** The street and unit lines together, for P.O. Box screening. */
function addressLines(form: IntakeFormData, prefix: AddressPrefix): string {
  return `${form[`${prefix}_street`]} ${form[`${prefix}_unit`]}`
}

/** One line for display, e.g. "123 Main St, Suite 4, Branson, MO 65616". */
function formatAddress(address: AddressParts): string {
  const stateZip = [address.state, address.zip].filter(Boolean).join(' ')
  return [address.street, address.unit, address.city, stateZip]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(', ')
}

/** Parses `data` against `schema`, falling back to an empty (all-optional) object on a mismatch. */
// The API stores each step's payload as sent, and Go's decoder reads a JSON
// null as "field absent". Drop nulls the same way so a saved step that Go
// accepted always parses here instead of coming back empty.
function withoutNulls(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(withoutNulls)
  if (typeof value !== 'object' || value === null) return value
  return Object.fromEntries(
    Object.entries(value)
      .filter(([, item]) => item !== null)
      .map(([key, item]) => [key, withoutNulls(item)]),
  )
}

function safeData<S extends z.ZodType>(schema: S, data: Record<string, unknown>): z.infer<S> {
  const parsed = schema.safeParse(withoutNulls(data))
  if (parsed.success) return parsed.data
  console.warn('Saved intake step did not match its schema', parsed.error.issues)
  return {} as z.infer<S>
}

// ---- Format validators, shared by both definitions --------------------------------------------

const einRegex = /^\d{2}-\d{7}$/
const dunsRegex = /^\d{9}$/
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const websiteRegex = /^(https?:\/\/)?[^\s./]+(\.[^\s./]+)+(\/\S*)?$/i

function todayLocal() {
  const now = new Date()
  const pad = (part: number) => String(part).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

/**
 * Every owner row with anything in it needs a full legal name (at least two
 * words) and, if given, a valid email. Blank rows are ignored, and at least one
 * owner must be named: Google background-checks every owner by name.
 */
function firstOwnersProblem(value: unknown): string | null {
  const rows = z.array(BusinessOwnerSchema).safeParse(value).data ?? []
  let named = 0
  for (const [index, row] of rows.entries()) {
    const name = row.full_name?.trim() ?? ''
    const email = row.email?.trim() ?? ''
    if (!name && !email && !(row.title?.trim() ?? '')) continue
    const label = rows.length > 1 ? `Owner ${index + 1}: ` : ''
    if (name.split(/\s+/).filter(Boolean).length < 2)
      return `${label}enter the full legal name, first and last.`
    if (email && !emailRegex.test(email))
      return `${label}enter an email address like name@example.com.`
    named++
  }
  return named ? null : 'Enter the full legal name of at least one owner.'
}

// A day in no hours group is stored closed, so a day that is open but has no
// times is either a fresh form (nothing picked yet) or a picked day still
// waiting for its times.
function firstHoursProblem(value: unknown): string | null {
  const rows = z.array(BusinessHoursSchema).safeParse(value).data ?? []
  const open = rows.filter((row) => row.closed !== true)
  if (!open.some(({ opens = '', closes = '' }) => opens !== '' || closes !== ''))
    return "Pick the days you're open and set their hours."
  for (const { day, opens = '', closes = '' } of open) {
    if (opens === '' || closes === '') return `${day}: set both an opening and a closing time.`
    if (!(opens < closes)) return `${day}: closing time must be after opening time.`
  }
  return null
}

// helpers.req is typed as a bare Function. It reports whether a value counts
// as entered: not empty, blank, null or undefined.
const filled = helpers.req as (value: unknown) => boolean

const einFormat = helpers.withMessage(
  'Enter the EIN as 12-3456789.',
  (v: unknown) => !filled(v) || einRegex.test(String(v).trim()),
)
const dunsFormat = helpers.withMessage(
  'Enter the 9-digit D-U-N-S number, like 12-345-6789.',
  (v: unknown) => !filled(v) || dunsRegex.test(String(v).replace(/[-\s]/g, '')),
)
const emailFormat = helpers.withMessage('Enter an email address like name@example.com.', email)
const phoneFormat = helpers.withMessage(
  'Enter a 10-digit phone number, like 417-555-0100.',
  (v: unknown) => {
    if (!filled(v)) return true
    const raw = String(v)
    if (!/^[\d\s().+-]+$/.test(raw)) return false
    const digits = raw.replace(/\D/g, '')
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))
  },
)
const websiteFormat = helpers.withMessage(
  'Enter a web address like example.com.',
  (v: unknown) => !filled(v) || websiteRegex.test(String(v)),
)
const zipFormat = helpers.withMessage(
  'Enter a 5-digit ZIP code, like 65616.',
  (v: unknown) => !filled(v) || /^\d{5}(-\d{4})?$/.test(String(v).trim()),
)
const notPOBoxFormat = helpers.withMessage(
  'Enter a physical street address, not a P.O. Box.',
  (v: unknown) => !filled(v) || !poBoxRegex.test(String(v)),
)
const dateNotFuture = helpers.withMessage(
  "The formation date can't be in the future.",
  (v: unknown) => !filled(v) || String(v) <= todayLocal(),
)
const wholeNumber = helpers.withMessage(
  'Enter a whole number, 0 or more.',
  (v: unknown) =>
    v === '' || v === undefined || v === null || (Number.isInteger(Number(v)) && Number(v) >= 0),
)
const amountNumber = helpers.withMessage(
  'Enter an amount of 0 or more.',
  (v: unknown) =>
    v === '' || v === undefined || v === null || (Number.isFinite(Number(v)) && Number(v) >= 0),
)
const zipsFormat = helpers.withMessage(
  ({ $model }: { $model: unknown }) =>
    `ZIP codes must be 5 digits. Check: ${strings(typeof $model === 'string' ? $model : '')
      .filter((zip) => !/^\d{5}$/.test(zip))
      .join(', ')}.`,
  (v: unknown) => !filled(v) || strings(String(v)).every((zip) => /^\d{5}$/.test(zip)),
)
const ownersFormat = helpers.withMessage(
  ({ $model }: { $model: unknown }) => firstOwnersProblem($model) ?? '',
  (v: unknown) => firstOwnersProblem(v) === null,
)
const hoursTableFormat = helpers.withMessage(
  ({ $model }: { $model: unknown }) => firstHoursProblem($model) ?? '',
  (v: unknown) => firstHoursProblem(v) === null,
)

// ---- Rule builders, shared by both definitions -------------------------------------------------

function requiredRule(message: string) {
  return helpers.withMessage(message, required)
}
function mustBeTrue(message: string) {
  return helpers.withMessage(message, (v: unknown) => v === true)
}
function answered(message: string) {
  return helpers.withMessage(message, (v: unknown) => v !== undefined)
}
function requiredIfHasEmployees(message: string) {
  return helpers.withMessage(
    message,
    (v: unknown, form: IntakeFormData) => form.has_employees !== true || filled(v),
  )
}
type ListFieldName = 'wanted_job_types' | 'wanted_services'

function atLeastOneOrOther(otherKey: 'other_wanted_work', message: string) {
  return helpers.withMessage(
    message,
    (v: unknown, form: IntakeFormData) =>
      (Array.isArray(v) && v.length > 0) || Boolean(form[otherKey]?.trim()),
  )
}
function capabilityAcknowledged(
  servicesKey: ListFieldName,
  otherKey: 'other_wanted_work',
  message: string,
) {
  return helpers.withMessage(message, (v: unknown, form: IntakeFormData) => {
    const hasSelection = form[servicesKey].length > 0
    const hasOther = Boolean(form[otherKey]?.trim())
    return !(hasSelection || hasOther) || v === true
  })
}

/** Street, city, state, and ZIP are required; unit is optional. `when` makes the whole address conditional. */
function addressRules(
  prefix: AddressPrefix,
  options: { notPOBox?: boolean; when?: (form: IntakeFormData) => boolean } = {},
): StepRules {
  const applies = options.when ?? (() => true)
  const required = (message: string) =>
    helpers.withMessage(message, (v: unknown, form: IntakeFormData) => !applies(form) || filled(v))
  const rules: Partial<Record<AddressFieldKey, Record<string, ValidationRule>>> = {
    [`${prefix}_street`]: {
      required: required('Enter the street address.'),
      ...(options.notPOBox === true ? { notPOBox: notPOBoxFormat } : {}),
    },
    [`${prefix}_city`]: { required: required('Enter the city.') },
    [`${prefix}_state`]: { required: required('Choose the state.') },
    [`${prefix}_zip`]: { required: required('Enter the ZIP code.'), format: zipFormat },
  }
  return rules
}

const entityChoiceRequired = requiredRule('Choose the entity type.')
const entityOtherRequired = helpers.withMessage(
  'Describe the entity type, e.g. Inc. or LLP.',
  (v: unknown, form: IntakeFormData) => form.entity_type_choice !== 'Other' || filled(v),
)
const businessEmailRule = {
  required: requiredRule('Enter the business email address.'),
  format: emailFormat,
}
const documentsAckRule = mustBeTrue(
  'Confirm you checked the documents for matching name and address.',
)
const capabilityAckMessage =
  'Confirm every checked job is insured, staffed, and permitted where required.'
const reviewAckRule = mustBeTrue('Confirm the intake is accurate before submitting.')

/** Legal-entity fields shared by Burns step 0 and generic step 1 (identity step). */
function identityRulesBase(): StepRules {
  return {
    legal_name: { required: requiredRule('Enter the exact legal name as filed.') },
    entity_type_choice: { required: entityChoiceRequired },
    entity_type_other: { required: entityOtherRequired },
    formation_state: { required: requiredRule('Choose the state of formation.') },
    formation_date: { notFuture: dateNotFuture },
    ein: { required: requiredRule('Enter the EIN.'), format: einFormat },
    duns: { format: dunsFormat },
    ...addressRules('physical_address', { notPOBox: true }),
    ...addressRules('mailing_address', { when: (form) => !form.mailing_same_as_physical }),
  }
}

function burnsIdentityRules(): StepRules {
  return {
    ...identityRulesBase(),
    business_email: businessEmailRule,
    owners: { complete: ownersFormat },
    owners_background_check_acknowledged: {
      required: mustBeTrue('Confirm every owner knows Google will run a background check.'),
    },
  }
}

function genericIdentityRules(): StepRules {
  return {
    ...identityRulesBase(),
    main_phone: { required: requiredRule('Enter the main phone number.'), format: phoneFormat },
    business_email: businessEmailRule,
    website: { format: websiteFormat },
    years_in_business: { format: wholeNumber },
  }
}

// ---- Identity step serialize/restore, shared by Burns step 0 and generic step 1 -----------------

function serializeIdentity(form: IntakeFormData): Record<string, unknown> {
  return {
    legal_name: form.legal_name,
    dba: form.dba,
    entity_type: joinEntityType(form.entity_type_choice, form.entity_type_other),
    formation_state: form.formation_state,
    formation_date: form.formation_date,
    ein: form.ein,
    duns: form.duns,
    physical_address: addressOf(form, 'physical_address'),
    mailing_same_as_physical: form.mailing_same_as_physical,
    // A mailing address marked the same is not sent, so it can't go stale.
    mailing_address: form.mailing_same_as_physical ? undefined : addressOf(form, 'mailing_address'),
    main_phone: form.main_phone,
    business_email: form.business_email,
    website: form.website,
    years_in_business: form.years_in_business,
  }
}

function restoreIdentity(form: IntakeFormData, data: Record<string, unknown>) {
  const { entity_type, physical_address, mailing_same_as_physical, mailing_address, ...rest } =
    safeData(BusinessIdentitySchema, data)
  const { choice, other } = splitEntityType(entity_type)
  Object.assign(form, rest, { entity_type_choice: choice, entity_type_other: other })
  restoreAddress(form, 'physical_address', physical_address)
  restoreAddress(form, 'mailing_address', mailing_address)
  // A one-line address saved before the checkbox existed had a mailing line only when it differed.
  if (mailing_same_as_physical !== undefined)
    form.mailing_same_as_physical = mailing_same_as_physical
  else if (typeof mailing_address === 'string')
    form.mailing_same_as_physical = !mailing_address.trim()
}

function genericInitial(): IntakeFormData {
  return {
    gbp_exists: undefined,
    gbp_verified: undefined,
    gbp_public: undefined,
    ...blankAddress('gbp_address'),
    gbp_po_box: undefined,
    legal_name: '',
    dba: '',
    entity_type_choice: '',
    entity_type_other: '',
    formation_state: '',
    formation_date: '',
    ein: '',
    duns: '',
    owners: [{ full_name: '', title: '', email: '' }],
    owners_background_check_acknowledged: undefined,
    ...blankAddress('physical_address'),
    mailing_same_as_physical: true,
    ...blankAddress('mailing_address'),
    main_phone: '',
    business_email: '',
    website: '',
    years_in_business: undefined,
    owner_legal_name: '',
    title: '',
    direct_phone: '',
    direct_email: '',
    background_check_contact: '',
    background_check_acknowledged: undefined,
    primary_lsa_category: '',
    wanted_job_types: [],
    unwanted_job_types: [],
    wanted_services: [],
    minimum_job_size: '',
    work_refused: '',
    broad_category_leads: undefined,
    other_wanted_work: '',
    other_unwanted_work: '',
    wanted_work_capability_acknowledged: undefined,
    primary_city: '',
    zip_codes_text: '',
    counties_text: '',
    max_drive_distance: undefined,
    exclusions: '',
    hours: hours(),
    answered_by: '',
    after_hours_handling: '',
    calls_recorded_acknowledged: undefined,
    weekly_budget: undefined,
    target_leads_per_week: undefined,
    average_job_value: undefined,
    licenses: [{ jurisdiction: '', type: '', number: '', expiry: '' }],
    local_requirements: [
      {
        jurisdiction: '',
        work_accepted: '',
        registration_required: undefined,
        registration_held: undefined,
        authority: '',
        number: '',
        expiry: '',
      },
    ],
    general_liability_carrier: '',
    policy_number: '',
    coverage_limits: '',
    policy_expiry: '',
    has_employees: undefined,
    workers_comp_carrier: '',
    workers_comp_policy_number: '',
    workers_comp_expiry: '',
    available_documents: [],
    name_address_consistency_acknowledged: undefined,
    local_licenses_confirmed: undefined,
    notes: '',
    owner_google_account: '',
    manager_access_acknowledged: undefined,
    gbp_access_available: undefined,
    existing_google_ads_account: undefined,
    existing_google_ads_account_details: '',
    review_acknowledged: undefined,
    review_notes: '',
    lsa_call_phone: '',
    business_hours: hours(),
    ad_hours: hours(),
  }
}

// Index-aligned with `generic.steps` below, used to validate a saved step's
// data before it is merged into the form on resume.
const genericStepSchemas: z.ZodType[] = [
  PrerequisitesSchema,
  BusinessIdentitySchema,
  OwnerContactSchema,
  ServicesSchema,
  ServiceAreaSchema,
  HoursResponsivenessSchema,
  BudgetSchema,
  LicensesSchema,
  InsuranceSchema,
  DocumentsStepSchema,
  GoogleAccessSchema,
  ReviewSchema,
]

function genericSerialize(step: number, form: IntakeFormData): Record<string, unknown> {
  switch (step) {
    case 0:
      return {
        ...pick(form, 'gbp_exists', 'gbp_verified', 'gbp_public'),
        gbp_address: addressOf(form, 'gbp_address'),
        gbp_po_box: form.gbp_po_box,
      }
    case 1:
      return serializeIdentity(form)
    case 2:
      return pick(
        form,
        'owner_legal_name',
        'title',
        'direct_phone',
        'direct_email',
        'background_check_contact',
        'background_check_acknowledged',
      )
    case 3:
      return pick(
        form,
        'primary_lsa_category',
        'wanted_job_types',
        'unwanted_job_types',
        'minimum_job_size',
        'work_refused',
        'broad_category_leads',
        'other_wanted_work',
        'other_unwanted_work',
        'wanted_work_capability_acknowledged',
      )
    case 4:
      return {
        primary_city: form.primary_city,
        zip_codes: strings(form.zip_codes_text),
        counties: strings(form.counties_text),
        max_drive_distance: number(form.max_drive_distance),
        exclusions: form.exclusions,
      }
    case 5:
      return {
        hours: form.hours.map((day) => ({ ...day, closed: Boolean(day.closed) })),
        answered_by: form.answered_by,
        after_hours_handling: form.after_hours_handling,
        calls_recorded_acknowledged: form.calls_recorded_acknowledged,
      }
    case 6:
      return {
        weekly_budget: number(form.weekly_budget),
        target_leads_per_week: number(form.target_leads_per_week),
        average_job_value: number(form.average_job_value),
      }
    case 7:
      return { licenses: form.licenses, local_requirements: form.local_requirements }
    case 8:
      return pick(
        form,
        'general_liability_carrier',
        'policy_number',
        'coverage_limits',
        'policy_expiry',
        'has_employees',
        'workers_comp_carrier',
        'workers_comp_policy_number',
        'workers_comp_expiry',
      )
    case 9:
      return pick(
        form,
        'available_documents',
        'name_address_consistency_acknowledged',
        'local_licenses_confirmed',
        'notes',
      )
    case 10:
      return pick(
        form,
        'owner_google_account',
        'manager_access_acknowledged',
        'gbp_access_available',
        'existing_google_ads_account',
        'existing_google_ads_account_details',
      )
    default:
      return { review_acknowledged: form.review_acknowledged, notes: form.review_notes }
  }
}

function genericRestore(step: number, form: IntakeFormData, data: Record<string, unknown>) {
  if (step === 0) {
    const { gbp_address, ...rest } = safeData(PrerequisitesSchema, data)
    Object.assign(form, rest)
    restoreAddress(form, 'gbp_address', gbp_address)
    return
  }
  if (step === 1) {
    restoreIdentity(form, data)
    return
  }
  if (step === 4) {
    const area = safeData(ServiceAreaSchema, data)
    Object.assign(form, area, {
      zip_codes_text: (area.zip_codes ?? []).join(', '),
      counties_text: (area.counties ?? []).join(', '),
    })
    return
  }
  if (step === 11) {
    const review = safeData(ReviewSchema, data)
    form.review_acknowledged = review.review_acknowledged
    form.review_notes = review.notes ?? ''
    return
  }
  const schema = genericStepSchemas[step]
  if (schema) Object.assign(form, safeData(schema, data))
}

function genericRules(step: number): StepRules {
  switch (step) {
    case 0:
      return {
        gbp_exists: {
          answered: answered('Choose whether the business has a Google Business Profile.'),
        },
        gbp_verified: {
          answered: answered('Choose whether the Google Business Profile is verified.'),
        },
        gbp_public: { answered: answered('Choose whether the Google Business Profile is public.') },
        ...addressRules('gbp_address'),
      }
    case 1:
      return genericIdentityRules()
    case 2:
      return {
        owner_legal_name: { required: requiredRule("Enter the owner's legal name.") },
        direct_phone: {
          required: requiredRule('Enter the direct phone number.'),
          format: phoneFormat,
        },
        direct_email: {
          required: requiredRule('Enter the direct email address.'),
          format: emailFormat,
        },
        background_check_acknowledged: {
          required: mustBeTrue('Confirm you consent to a background check.'),
        },
      }
    case 3:
      return {
        wanted_job_types: {
          atLeastOne: atLeastOneOrOther(
            'other_wanted_work',
            'Check at least one job type, or describe other work you want leads for.',
          ),
        },
        wanted_work_capability_acknowledged: {
          required: capabilityAcknowledged(
            'wanted_job_types',
            'other_wanted_work',
            capabilityAckMessage,
          ),
        },
      }
    case 4:
      return {
        primary_city: { required: requiredRule('Enter the primary city.') },
        zip_codes_text: {
          required: requiredRule('Enter at least one ZIP code.'),
          format: zipsFormat,
        },
        max_drive_distance: { format: wholeNumber },
      }
    case 5:
      return {
        hours: { format: hoursTableFormat },
        calls_recorded_acknowledged: {
          required: mustBeTrue('Confirm you understand calls may be recorded.'),
        },
      }
    case 6:
      return {
        weekly_budget: { required: requiredRule('Enter the weekly budget.'), format: amountNumber },
        target_leads_per_week: { format: wholeNumber },
        average_job_value: { format: amountNumber },
      }
    case 7:
      return {}
    case 8:
      return {
        general_liability_carrier: {
          required: requiredRule('Enter the general liability insurance carrier.'),
        },
        policy_number: { required: requiredRule('Enter the policy number.') },
        policy_expiry: { required: requiredRule('Enter the policy expiration date.') },
        workers_comp_carrier: {
          required: requiredIfHasEmployees("Enter the workers' compensation carrier."),
        },
        workers_comp_policy_number: {
          required: requiredIfHasEmployees("Enter the workers' compensation policy number."),
        },
        workers_comp_expiry: {
          required: requiredIfHasEmployees(
            "Enter the workers' compensation policy expiration date.",
          ),
        },
      }
    case 9:
      return { name_address_consistency_acknowledged: { required: documentsAckRule } }
    case 10:
      return {
        owner_google_account: {
          required: requiredRule("Enter the owner's Google account email address."),
          format: emailFormat,
        },
        manager_access_acknowledged: {
          required: mustBeTrue(
            'Confirm you will grant manager access to the Google Business Profile and Google Ads account.',
          ),
        },
      }
    default:
      return { review_acknowledged: { required: reviewAckRule } }
  }
}

const generic: IntakeFormDefinition = {
  id: 'lsa',
  initial: genericInitial,
  serialize: genericSerialize,
  restore: genericRestore,
  rules: genericRules,
  blocksFirstAdvance: (form) =>
    form.gbp_exists !== true ||
    form.gbp_verified !== true ||
    form.gbp_public !== true ||
    form.gbp_po_box === true ||
    poBoxRegex.test(addressLines(form, 'gbp_address')),
  steps: [
    { title: 'Prerequisites', kind: 'prerequisites' },
    { title: 'Business identity', kind: 'identity' },
    { title: 'Owner & contact', kind: 'owner' },
    { title: 'Services', kind: 'services' },
    { title: 'Service area', kind: 'service-area' },
    { title: 'Hours', kind: 'generic-hours' },
    { title: 'Budget', kind: 'budget' },
    { title: 'Licenses', kind: 'licenses' },
    { title: 'Insurance', kind: 'insurance' },
    { title: 'Documents', kind: 'documents' },
    { title: 'Google access', kind: 'google-access' },
    { title: 'Review', kind: 'review' },
  ],
}

// Index-aligned with `burns.steps` below.
const burnsStepSchemas: z.ZodType[] = [
  BusinessIdentitySchema,
  BurnsServicesSchema,
  DocumentsStepSchema,
  LSAContactSchema,
  BurnsHoursSchema,
  ReviewSchema,
]

function burnsRules(step: number): StepRules {
  switch (step) {
    case 0:
      return burnsIdentityRules()
    case 1:
      return {
        wanted_services: {
          atLeastOne: atLeastOneOrOther(
            'other_wanted_work',
            'Check at least one service, or describe other work you want leads for.',
          ),
        },
        wanted_work_capability_acknowledged: {
          required: capabilityAcknowledged(
            'wanted_services',
            'other_wanted_work',
            capabilityAckMessage,
          ),
        },
      }
    case 2:
      return { name_address_consistency_acknowledged: { required: documentsAckRule } }
    case 3:
      return {
        lsa_call_phone: {
          required: requiredRule('Enter the LSA call phone number.'),
          format: phoneFormat,
        },
      }
    case 4:
      return {
        business_hours: { format: hoursTableFormat },
        ad_hours: { format: hoursTableFormat },
      }
    default:
      return { review_acknowledged: { required: reviewAckRule } }
  }
}

const burns: IntakeFormDefinition = {
  id: 'burns-construction',
  initial: genericInitial,
  serialize: (step, form) => {
    switch (step) {
      case 0:
        return {
          ...serializeIdentity(form),
          owners: form.owners,
          owners_background_check_acknowledged: form.owners_background_check_acknowledged,
        }
      case 1:
        return pick(
          form,
          'wanted_services',
          'other_wanted_work',
          'minimum_job_size',
          'wanted_work_capability_acknowledged',
        )
      case 2:
        return pick(
          form,
          'available_documents',
          'name_address_consistency_acknowledged',
          'local_licenses_confirmed',
          'notes',
        )
      case 3:
        return pick(form, 'lsa_call_phone')
      case 4:
        return {
          business_hours: form.business_hours.map((day) => ({
            ...day,
            closed: Boolean(day.closed),
          })),
          ad_hours: form.ad_hours.map((day) => ({ ...day, closed: Boolean(day.closed) })),
        }
      default:
        return { review_acknowledged: form.review_acknowledged, notes: form.review_notes }
    }
  },
  restore: (step, form, data) => {
    if (step === 0) {
      restoreIdentity(form, data)
      // An intake saved before owners were asked for still gets one blank row to fill.
      if (form.owners.length === 0) form.owners = [{ full_name: '', title: '', email: '' }]
      return
    }
    if (step === 5) {
      const review = safeData(ReviewSchema, data)
      form.review_acknowledged = review.review_acknowledged
      form.review_notes = review.notes ?? ''
      return
    }
    const schema = burnsStepSchemas[step]
    if (schema) Object.assign(form, safeData(schema, data))
  },
  rules: burnsRules,
  steps: [
    { title: 'Legal entity', kind: 'identity' },
    { title: 'Services', kind: 'burns-services' },
    { title: 'Verification documents', kind: 'documents' },
    { title: 'LSA contact', kind: 'burns-contact' },
    { title: 'Business and ad hours', kind: 'burns-hours' },
    { title: 'Review', kind: 'review' },
  ],
}

// ---- Address warnings -------------------------------------------------------------------------

/**
 * Warnings for the address fields: a street line that looks incomplete, a ZIP
 * outside its state, and a physical address that differs from the Google
 * Business Profile address. They never block a step; a real rural address can
 * look incomplete. The server repeats the same checks in the submission summary
 * (apps/api/intake/address.go).
 */
export function addressFieldWarnings(
  form: IntakeFormData,
): Partial<Record<AddressPrefix, string[]>> {
  const warnings: Partial<Record<AddressPrefix, string[]>> = {}
  const prefixes: AddressPrefix[] = form.mailing_same_as_physical
    ? ['gbp_address', 'physical_address']
    : ['gbp_address', 'physical_address', 'mailing_address']
  for (const prefix of prefixes) {
    const warning = addressWarning(addressOf(form, prefix))
    if (warning !== undefined) warnings[prefix] = [warning]
  }
  const gbp = addressOf(form, 'gbp_address')
  const physical = addressOf(form, 'physical_address')
  if (addressEntered(gbp) && addressEntered(physical) && !sameAddress(gbp, physical)) {
    ;(warnings.physical_address ??= []).push(
      `This doesn't match the Google Business Profile address, “${formatAddress(gbp)}”. If they're the same place, enter it the same way in both.`,
    )
  }
  return warnings
}

export function intakeDefinition(id: string): IntakeFormDefinition | undefined {
  return { [generic.id]: generic, [burns.id]: burns }[id]
}
